import { AxiosError } from "axios";
import api from "../api";

import { User } from "../interfaces/users.interface";
import { getSavedState, setSavedState } from "../utils/LocalStorage.utils";
import { Transporter, ReferralStats } from "../interfaces/transporters.interface";
import LSVariables from "../configs/LS_variables.json";

interface SignupData {
  email: string;
  password: string;
  companyName?: string;
  name: string;
  lastName?: string;
  phone?: string;
  address?: string;
  SIRETNumber?: number;
  ZIPCode?: number;
  country?: string;
  referralCode?: string; // Added referral code field
}

function isAxiosError(error: any): error is AxiosError {
  return error.isAxiosError === true;
}

export class AuthService {
  getUser(): Transporter | null {
    const user = getSavedState(LSVariables.saved_user) as Transporter | null;
    return user ? this.parseTransporterDates(user) : null;
  }

  async refreshUser(): Promise<Transporter> {
    try {
      const response = await api.get('/transporters/me');
      if (response.data && response.data.data) {
        const userData = this.parseTransporterDates(response.data.data);
        setSavedState(userData, LSVariables.saved_user);
        return userData;
      }
      throw new Error('Invalid response data');
    } catch (error) {
      console.error("Error refreshing user data:", error);
      
      // If we get a 409 (Conflict) or 401 (Unauthorized), clear the stored data
      if (isAxiosError(error) && (error.response?.status === 409 || error.response?.status === 401)) {
        console.warn("Invalid transporter data detected, clearing stored user data");
        this.logOut();
      }
      
      throw error;
    }
  }

  async getReferralStats(): Promise<ReferralStats> {
    try {
      const response = await api.get('/transporters/referral-stats');
      return response.data.data;
    } catch (error) {
      console.error("Error fetching referral stats:", error);
      throw error;
    }
  }

  async logIn(user: User) {
    try {
      const response = await api.post(`/loginTransporter`, user);
      if (response.data && response.data.data) {
        const userData = this.parseTransporterDates(response.data.data);
        setSavedState(userData, LSVariables.saved_user);
        // Store the token in localStorage
        localStorage.setItem('token', response.data.token);
      }
      return response;
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  }

  logOut() {
    // Clear user data and token from localStorage
    setSavedState(null, LSVariables.saved_user);
    localStorage.removeItem('token');
    document.cookie = "Authorization=; Max-age=0; path=/;";
    return api.post(`/logout`);
    
  }

  signup(data: SignupData) {
    return api.post(`/signup/transporter-app`, data);
  }

  async updateTransporter(data: Partial<Transporter>, logoFile?: File) {
    try {
      let formData: FormData | null = null;
      
      if (logoFile) {
        formData = new FormData();
        formData.append('logo', logoFile);
        
        // Handle number fields explicitly
        if (data.SIRETNumber !== undefined) {
          formData.append('SIRETNumber', data.SIRETNumber.toString());
        }
        if (data.ZIPCode !== undefined) {
          formData.append('ZIPCode', data.ZIPCode.toString());
        }
        
        // Handle other fields
        if (data.name) formData.append('name', data.name);
        if (data.lastName) formData.append('lastName', data.lastName);
        if (data.phone) formData.append('phone', data.phone);
        if (data.address) formData.append('address', data.address);
        if (data.country) formData.append('country', data.country);
        if (data.companyName) formData.append('companyName', data.companyName);
      }

      const response = await api.put(
        `/transporters/self`,
        formData || this.serializeTransporterData(data),
        formData ? {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        } : undefined
      );

      const currentUser = this.getUser();
      if (!currentUser) {
        throw new Error("No user data found");
      }
      const updatedUser = this.parseTransporterDates({ ...currentUser, ...data, ...response.data.data });
      setSavedState(updatedUser, LSVariables.saved_user);
      return updatedUser;
    } catch (error: unknown) {
      console.error("Error updating transporter:", error);
      if (isAxiosError(error)) {
        throw new Error(error.response?.data?.message || "An error occurred while updating the transporter");
      } else {
        throw new Error("An unexpected error occurred");
      }
    }
  }

  async updateTransporterSubscription(subscriptionData: Partial<Transporter>) {
    try {
      const currentUser = this.getUser();
      if (!currentUser) {
        throw new Error("No user data found");
      }
      const response = await api.put(
        `/transporters/${currentUser._id}/subscription`,
        this.serializeTransporterData(subscriptionData)
      );
      const updatedUser = this.parseTransporterDates({ ...currentUser, ...subscriptionData, ...response.data });
      setSavedState(updatedUser, LSVariables.saved_user);
      return updatedUser;
    } catch (error: unknown) {
      console.error("Error updating transporter subscription:", error);
      if (isAxiosError(error)) {
        throw new Error(error.response?.data?.message || "An error occurred while updating the transporter subscription");
      } else {
        throw new Error("An unexpected error occurred");
      }
    }
  }

  private serializeTransporterData(data: Partial<Transporter>): Record<string, any> {
    const serializedData: Record<string, any> = { ...data };
    if (serializedData.startDate instanceof Date) {
      serializedData.startDate = serializedData.startDate.toISOString();
    }
    if (serializedData.endDate instanceof Date) {
      serializedData.endDate = serializedData.endDate.toISOString();
    }
    if (serializedData.creationDate instanceof Date) {
      serializedData.creationDate = serializedData.creationDate.toISOString();
    }
    return serializedData;
  }

  private parseTransporterDates(data: Partial<Transporter>): Transporter {
    if (!data) {
      throw new Error("Invalid transporter data: data is null or undefined");
    }

    const parsedData = { ...data } as Transporter;
    if (typeof parsedData.startDate === 'string') {
      parsedData.startDate = new Date(parsedData.startDate);
    }
    if (typeof parsedData.endDate === 'string') {
      parsedData.endDate = new Date(parsedData.endDate);
    }
    if (typeof parsedData.creationDate === 'string') {
      parsedData.creationDate = new Date(parsedData.creationDate);
    }
    // Ensure all required properties are present
    if (!parsedData._id || !parsedData.name || !parsedData.email || !parsedData.subscriptionType) {
      throw new Error("Invalid transporter data: missing required properties");
    }
    return parsedData;
  }
}

export const authService = new AuthService();
